import React from "react";
import { CircleNotch, FilePdf, CheckCircle } from "@phosphor-icons/react";

/**
 * Full-screen overlay shown while the IdeaLab PDF is being generated and downloaded.
 * Stops the user thinking the screen has gone blank.
 *
 * Props:
 *   open  : boolean — render the overlay
 *   stage : "preparing" | "downloading" | "done" — controls headline + icon
 *   reference: optional reference number to show in the subhead
 */
export default function PdfDownloadOverlay({ open, stage = "preparing", reference }) {
  if (!open) return null;

  const isDone = stage === "done";
  const headline = isDone
    ? "Your report is ready"
    : stage === "downloading"
      ? "Downloading your discovery PDF…"
      : "Preparing your discovery PDF…";
  const sub = isDone
    ? "Check your downloads folder."
    : "This usually takes 5–10 seconds. Please don't close this tab.";

  return (
    <div
      className="fixed inset-0 z-[100] bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center px-6"
      role="dialog"
      aria-live="polite"
      aria-busy={!isDone}
      data-testid="pdf-download-overlay"
    >
      <div className="bg-white border border-zinc-200 max-w-md w-full p-8 md:p-10 text-center">
        <div className="flex items-center justify-center mb-5">
          {isDone ? (
            <CheckCircle size={42} weight="fill" className="text-emerald-500" />
          ) : (
            <div className="relative">
              <FilePdf size={42} weight="fill" className="text-zinc-300" />
              <CircleNotch size={56} className="animate-spin text-[hsl(var(--appistry-signal))] absolute -inset-3" />
            </div>
          )}
        </div>
        <p className="overline mb-3" data-testid="pdf-overlay-ref">
          Appistry IdeaLab{reference ? ` · ${reference}` : ""}
        </p>
        <h2
          className="text-xl md:text-2xl font-bold tracking-tight mb-2"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="pdf-overlay-headline"
        >
          {headline}
        </h2>
        <p className="text-sm text-zinc-600">{sub}</p>
      </div>
    </div>
  );
}
